/**
 * useStudioFeed — React hook that owns a single WebSocket connection to
 * /api/studio/ws/feed and exposes:
 *   { connected, events, subscribe(channelIds), stats }
 *
 * Auto-reconnects with backoff. Replays last events on (re)connect from the
 * server side. Consumers subscribe to the channel ids they care about.
 *
 * Usage:
 *   const { events, connected, subscribe } = useStudioFeed();
 *   useEffect(() => subscribe(activeChannelIds), [activeChannelIds]);
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || '';

function wsUrl() {
  const u = new URL(API, window.location.href);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/studio/ws/feed";
  return u.toString();
}

// Read session_token from document.cookie (httpOnly cookie is normally
// not readable from JS — but we also pass token via ?token=... below so
// the server accepts either path).
function readCookie(name) {
  const m = document.cookie.match(new RegExp(`(^|; )${name}=([^;]+)`));
  return m ? decodeURIComponent(m[2]) : null;
}

export default function useStudioFeed({ maxEvents = 120 } = {}) {
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const wsRef = useRef(null);
  const channelsRef = useRef([]);
  const reconnectRef = useRef(null);
  const backoffRef = useRef(1000);

  const pushEvents = useCallback((evts) => {
    setEvents((prev) => {
      const next = [...evts.filter(Boolean), ...prev];
      return next.slice(0, maxEvents);
    });
  }, [maxEvents]);

  const connect = useCallback(async () => {
    if (wsRef.current) return;
    // Best-effort: ask backend for our session token (the /me endpoint may
    // not return it; in that case the cookie is sent automatically by the
    // browser on the wss:// upgrade request).
    let token = readCookie("session_token");
    if (!token) {
      try {
        const r = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        token = r.data?.session_token || null;
      } catch { /* not signed in */ }
    }
    const url = token ? `${wsUrl()}?token=${encodeURIComponent(token)}` : wsUrl();
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      backoffRef.current = 1000;
      if (channelsRef.current.length) {
        ws.send(JSON.stringify({ op: "subscribe", channel_ids: channelsRef.current }));
      }
    };
    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        if (data.type === "replay" && Array.isArray(data.events)) {
          pushEvents(data.events);
        } else if (data.type && data.type !== "hello" && data.type !== "subscribed" && data.type !== "pong") {
          pushEvents([data]);
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Exponential backoff reconnect, cap at 15s
      const ms = Math.min(backoffRef.current, 15000);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
      reconnectRef.current = setTimeout(() => connect(), ms);
    };
    ws.onerror = () => {
      try { ws.close(); } catch { /* noop */ }
    };
  }, [pushEvents]);

  useEffect(() => {
    connect();
    const pingId = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) {
        try { wsRef.current.send(JSON.stringify({ op: "ping" })); } catch { /* noop */ }
      }
    }, 25000);
    return () => {
      clearInterval(pingId);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [connect]);

  const subscribe = useCallback((channelIds) => {
    const arr = Array.isArray(channelIds) ? channelIds.filter(Boolean) : [];
    channelsRef.current = arr;
    if (wsRef.current && wsRef.current.readyState === 1) {
      try {
        wsRef.current.send(JSON.stringify({ op: "subscribe", channel_ids: arr }));
      } catch { /* noop */ }
    }
  }, []);

  return { connected, events, subscribe };
}

/** Human-readable label for an event type. */
export function eventLabel(ev) {
  const map = {
    content_field_edited: "edited a field",
    seo_job_complete: "ran SEO Assistant",
    seo_applied: "applied SEO to a project",
    asset_uploaded: "uploaded an asset",
    asset_replaced: "replaced an asset",
    content_created: "added new content",
    approval_decided: "approved/declined a stage",
  };
  return map[ev?.type] || ev?.type || "did something";
}
